import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsIsoDateTime,
  parseAsString,
  parseAsStringLiteral,
} from "nuqs";

import { z } from "zod";

import { QUERY_PARAMS } from "./constants";
import { createBase64JsonParser } from "./customParsers";

// Shared between "dispatch" and "teleop"
export const commonParsers = {
  [QUERY_PARAMS.COMMON.PARENT_ORIGIN]: parseAsString,
  [QUERY_PARAMS.COMMON.ACTIVE_VEHICLE]: parseAsString,
  [QUERY_PARAMS.COMMON.SELECTED_MARKER]: parseAsString,
  [QUERY_PARAMS.COMMON.MARKERS_AFTER]: parseAsIsoDateTime,
  [QUERY_PARAMS.COMMON.SEARCH_TERM]: parseAsString,
};

export const teleopParsers = {
  ...commonParsers,
  [QUERY_PARAMS.TELEOP.FROM]: parseAsStringLiteral(["dispatch"]),
  [QUERY_PARAMS.TELEOP.DFR]: parseAsBoolean,
  [QUERY_PARAMS.TELEOP.VIEW_ONLY]: parseAsBoolean,
  [QUERY_PARAMS.TELEOP.RTIC]: parseAsBoolean,
  [QUERY_PARAMS.TELEOP.SELECTED_SKILL]: parseAsString,
  [QUERY_PARAMS.TELEOP.MISSION_TO_EDIT]: parseAsString,
  [QUERY_PARAMS.TELEOP.MULTIDRONE_AUTOLAUNCH]: parseAsBoolean,
  [QUERY_PARAMS.TELEOP.SUPPRESS_POST_FLIGHT_SURVEY]: parseAsBoolean,
};

/**
 * In a multidrone world, we might have individual RFD panels, each with their own query params.
 * This allows us to capture it and provide it when hydrating RFD iFrame.
 *
 * This is now encoded as base64url in the URL for cleaner query parameters.
 */
const teleopMetaSchema = z.object({
  [QUERY_PARAMS.TELEOP.SELECTED_SKILL]: z.string().nullish(),
  [QUERY_PARAMS.TELEOP.MISSION_TO_EDIT]: z.string().nullish(),
  [QUERY_PARAMS.TELEOP.VIEW_ONLY]: z.boolean().nullish(),
});

const teleopMetaMapSchema = z.record(z.string(), teleopMetaSchema);

const teleopMetaMapParser = createBase64JsonParser(
  teleopMetaMapSchema,
  undefined, // default equality
  (error, rawQuery) => {
    // Log corrupted teleopMetaMap data for debugging
    console.error("[teleopMetaMap] Failed to parse query param:", {
      error,
      rawQuery: rawQuery.slice(0, 100), // Truncate for logging
    });
  }
);

/**
 * Map of vehicleId -> custom URL for RFD panels.
 * Allows each vehicle's RFD panel to use a different custom URL.
 * Encoded as base64url in the URL for cleaner query parameters.
 */
const rfdUrlMapSchema = z.record(z.string(), z.string());

const rfdUrlMapParser = createBase64JsonParser(
  rfdUrlMapSchema,
  undefined, // default equality
  (error, rawQuery) => {
    console.error("[rfdUrlMap] Failed to parse query param:", {
      error,
      rawQuery: rawQuery.slice(0, 100),
    });
  }
);

export const dispatchParsers = {
  ...commonParsers,
  [QUERY_PARAMS.DISPATCH.VEHICLES]: parseAsArrayOf(parseAsString),
  [QUERY_PARAMS.DISPATCH.RESPOND]: parseAsString,
  [QUERY_PARAMS.DISPATCH.MISSION_TO_LAUNCH]: parseAsString,
  [QUERY_PARAMS.DISPATCH.OPERATION_UUID]: parseAsString,
  // Dispatch owns RTIC mode state for real-time-ops URL persistence.
  [QUERY_PARAMS.TELEOP.RTIC]: parseAsBoolean,
  [QUERY_PARAMS.DISPATCH.TELEOP_META_MAP]: teleopMetaMapParser,
  [QUERY_PARAMS.DISPATCH.RFD_URL_MAP]: rfdUrlMapParser,
  [QUERY_PARAMS.DISPATCH.MARKER_UUID]: parseAsString,
};
